import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Loader from '@/components/ui/Loader';
import {
  formatFileSize,
  isRetryableUploadError,
  type EvergreenMp4UploadError,
} from '@/lib/evergreenMp4File';
import { runEvergreenMp4Upload } from '@/lib/evergreenMp4Upload';
import { waitForEvergreenConversion } from '@/lib/evergreenLiveSource';
import { retryWhileSourceTimelinePreparing } from '@/lib/evergreenConvertError';
import {
  withReplacementDeclaration,
  type Mp4ReplacementDeclaration,
} from '@/lib/evergreenMp4Replacement';
import { buildMp4SourcesInput, toSourcesSaveError } from '@/lib/evergreenSources';
import { secondsToTimeString } from '@/lib/timelineOffset';
import type {
  EvergreenInteractionOutsideVideo,
  EvergreenInteractionSourceInput,
} from '@/features/webinar/types';
import { webinarService } from '@/services/webinar.service';

type Phase =
  | { kind: 'uploading'; sentBytes: number }
  | { kind: 'validating' }
  | { kind: 'saving' }
  | { kind: 'converting' }
  | { kind: 'outsideVideo'; interactions: EvergreenInteractionOutsideVideo[] }
  | { kind: 'conversionStatusUnavailable' }
  | { kind: 'error'; error: EvergreenMp4UploadError };

export type EvergreenMp4UploadOutcome =
  | 'ready'
  | 'pendingPromotion'
  | 'failed'
  | 'cancelled'
  | 'conversionBlocked'
  | 'conversionPending'
  | 'conversionStatusUnavailable';

const INTERACTION_LABEL_KEYS: Record<EvergreenInteractionOutsideVideo['eventType'], string> = {
  OFFER: 'contentLiveForm.eventTypes.offer',
  SURVEY: 'contentLiveForm.eventTypes.survey',
  QUIZ: 'contentLiveForm.eventTypes.quiz',
  RESOURCE_SHOW: 'contentLiveForm.eventTypes.sharedResource',
  PURCHASE_NOTIFICATION: 'contentLiveForm.eventTypes.purchaseNotification',
};

export function InteractionsOutsideVideoList({
  interactions,
}: {
  interactions: EvergreenInteractionOutsideVideo[];
}) {
  const { t } = useTranslation('createWebinar');
  return (
    <div className="flex w-full flex-col min-w-0">
      <div className="flex items-center bg-subtitle p-4 rounded-t-lg shrink-0">
        <div className="text-sm text-secondary w-20 shrink-0">{t('contentLiveForm.colTime')}</div>
        <div className="text-sm text-secondary flex-1 min-w-0">{t('contentLiveForm.colEvent')}</div>
      </div>
      <div className="max-h-64 overflow-y-auto rounded-b-lg bg-white">
        {interactions.map((interaction) => (
          <div key={interaction.eventId} className="flex items-center p-4">
            <div className="text-sm text-secondary w-20 shrink-0">
              {secondsToTimeString(interaction.startOffsetSeconds)}
            </div>
            <div className="text-black text-sm flex-1 min-w-0 truncate">
              {t(INTERACTION_LABEL_KEYS[interaction.eventType])}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type Props = {
  open: boolean;
  workspaceId: number;
  webinarId: number;
  file: File;
  // Source Live YouTube : la validation du MP4 lance la récupération des interactions du live.
  waitForConversion?: boolean;
  replacesVideo?: boolean;
  /** Nature du remplacement (et passages retirés), transmise à l'initialisation de l'upload. */
  replacement?: Mp4ReplacementDeclaration | null;
  /** Source d'interactions enregistrée avec la vidéo. Absente, celle en place est conservée. */
  interactionSource?: EvergreenInteractionSourceInput | null;
  onFinished: (outcome: EvergreenMp4UploadOutcome) => void;
};

export default function EvergreenMp4UploadDialog({
  open,
  workspaceId,
  webinarId,
  file,
  waitForConversion = false,
  replacesVideo = false,
  replacement = null,
  interactionSource = null,
  onFinished,
}: Props) {
  const { t, i18n } = useTranslation('createWebinar');
  const [phase, setPhase] = useState<Phase>({ kind: 'uploading', sentBytes: 0 });
  const [attempt, setAttempt] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  const onFinishedRef = useRef(onFinished);
  const interactionSourceRef = useRef(interactionSource);
  // Vidéo déjà validée : réessayer ne relance que l'enregistrement, pas l'envoi du fichier.
  const validatedVideoRef = useRef<{ file: File; videoResourceId: number } | null>(null);
  useEffect(() => {
    onFinishedRef.current = onFinished;
    interactionSourceRef.current = interactionSource;
  }, [onFinished, interactionSource]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    const uploadVideo = async (): Promise<number | null> => {
      if (validatedVideoRef.current?.file === file)
        return validatedVideoRef.current.videoResourceId;
      const result = await runEvergreenMp4Upload({
        file,
        signal: controller.signal,
        api: {
          init: (input) =>
            webinarService.initEvergreenMp4Upload(
              workspaceId,
              webinarId,
              withReplacementDeclaration(input, replacement),
            ),
          getPartUrls: (uploadId, request) =>
            webinarService.getEvergreenMp4UploadPartUrls(workspaceId, webinarId, uploadId, request),
          complete: (uploadId, input) =>
            webinarService.completeEvergreenMp4Upload(workspaceId, webinarId, uploadId, input),
          getStatus: (uploadId) =>
            webinarService.getEvergreenMp4UploadStatus(workspaceId, webinarId, uploadId),
        },
        onProgress: (sentBytes) => setPhase({ kind: 'uploading', sentBytes }),
        onValidating: () => setPhase({ kind: 'validating' }),
      });
      if (controller.signal.aborted || result.kind === 'aborted') return null;
      if (result.kind === 'pendingPromotion') {
        onFinishedRef.current('pendingPromotion');
        return null;
      }
      if (result.kind !== 'ready') {
        setPhase({ kind: 'error', error: result.error });
        return null;
      }
      if (result.videoResourceId == null) {
        setPhase({ kind: 'error', error: 'INTERNAL_ERROR' });
        return null;
      }
      validatedVideoRef.current = { file, videoResourceId: result.videoResourceId };
      return result.videoResourceId;
    };

    void (async () => {
      const videoResourceId = await uploadVideo();
      if (videoResourceId == null || controller.signal.aborted) return;
      // La vidéo validée ne remplace la vidéo en place qu'une fois enregistrée comme source.
      setPhase({ kind: 'saving' });
      try {
        await retryWhileSourceTimelinePreparing(
          () =>
            webinarService.updateEvergreenSources(
              workspaceId,
              webinarId,
              buildMp4SourcesInput(videoResourceId, interactionSourceRef.current),
            ),
          { signal: controller.signal },
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          setPhase({ kind: 'error', error: toSourcesSaveError(error) });
        }
        return;
      }
      if (controller.signal.aborted) return;
      if (!waitForConversion) {
        onFinishedRef.current('ready');
        return;
      }
      setPhase({ kind: 'converting' });
      const conversion = await waitForEvergreenConversion({
        loadConfiguration: () => webinarService.getEvergreenConfiguration(workspaceId, webinarId),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (conversion.kind === 'blocked') {
        setPhase({ kind: 'outsideVideo', interactions: conversion.interactions });
        return;
      }
      if (conversion.kind === 'unavailable') {
        setPhase({ kind: 'conversionStatusUnavailable' });
        return;
      }
      onFinishedRef.current(conversion.kind === 'ready' ? 'ready' : 'conversionPending');
    })();
    return () => controller.abort();
  }, [open, file, workspaceId, webinarId, attempt, waitForConversion, replacement]);

  // La vidéo validée n'est enregistrée comme source que par cet onglet : le fermer avant la fin de
  // l'enregistrement laisse la vidéo actuelle en place.
  const guardsTab =
    open && (phase.kind === 'uploading' || phase.kind === 'validating' || phase.kind === 'saving');
  useEffect(() => {
    if (!guardsTab) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [guardsTab]);

  const cancel = () => {
    controllerRef.current?.abort();
    onFinishedRef.current('cancelled');
  };

  const retry = () => {
    setPhase({ kind: 'uploading', sentBytes: 0 });
    setAttempt((value) => value + 1);
  };

  const percent =
    phase.kind === 'uploading' && file.size > 0
      ? Math.min(100, Math.floor((phase.sentBytes / file.size) * 100))
      : 100;

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        {phase.kind === 'outsideVideo' ? (
          <DialogHeader>
            <DialogTitle>{t('sessionForm.mp4.dialog.outsideVideoTitle')}</DialogTitle>
            <DialogDescription>{t('sessionForm.mp4.dialog.outsideVideoHint')}</DialogDescription>
          </DialogHeader>
        ) : (
          <DialogHeader>
            <DialogTitle>
              {t(
                replacesVideo
                  ? 'sessionForm.mp4.replace.uploadTitle'
                  : 'sessionForm.mp4.dialog.title',
              )}
            </DialogTitle>
            <DialogDescription className="truncate">{file.name}</DialogDescription>
          </DialogHeader>
        )}

        {phase.kind === 'uploading' && (
          <>
            <div className="flex w-full flex-col gap-2">
              <p className="text-sm text-secondary">{t('sessionForm.mp4.dialog.uploadingHint')}</p>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              >
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="flex justify-between text-sm text-secondary">
                <span>{percent} %</span>
                <span>
                  {formatFileSize(phase.sentBytes, i18n.language)} /{' '}
                  {formatFileSize(file.size, i18n.language)}
                </span>
              </div>
            </div>
            <DialogFooter className="w-full">
              <Button type="button" variant="outline" className="w-full" onClick={cancel}>
                {t('sessionForm.mp4.dialog.cancel')}
              </Button>
            </DialogFooter>
          </>
        )}

        {(phase.kind === 'validating' ||
          phase.kind === 'saving' ||
          phase.kind === 'converting') && (
          <div className="flex items-center gap-2 text-secondary">
            <Loader />
            <p>{t(`sessionForm.mp4.dialog.${phase.kind}`)}</p>
          </div>
        )}

        {phase.kind === 'outsideVideo' && (
          <>
            <InteractionsOutsideVideoList interactions={phase.interactions} />
            <DialogFooter className="w-full">
              <Button
                type="button"
                className="w-full"
                onClick={() => onFinishedRef.current('conversionBlocked')}
              >
                {t('sessionForm.mp4.dialog.goToInteractions')}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase.kind === 'conversionStatusUnavailable' && (
          <>
            <p className="text-sm text-secondary">
              {t('sessionForm.mp4.dialog.conversionStatusUnavailable')}
            </p>
            <DialogFooter className="w-full">
              <Button
                type="button"
                className="w-full"
                onClick={() => onFinishedRef.current('conversionStatusUnavailable')}
              >
                {t('sessionForm.mp4.dialog.close')}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase.kind === 'error' && (
          <>
            <p className="text-error">{t(`sessionForm.mp4.errors.${phase.error}`)}</p>
            {replacesVideo && (
              <p className="text-sm text-secondary">
                {t('sessionForm.mp4.replace.currentVideoKept')}
              </p>
            )}
            <DialogFooter className="w-full flex justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => onFinishedRef.current('failed')}
              >
                {t('sessionForm.mp4.dialog.close')}
              </Button>
              {isRetryableUploadError(phase.error) && (
                <Button type="button" className="flex-1" onClick={retry}>
                  {t('sessionForm.mp4.dialog.retry')}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
